import { memo, useMemo, useState, useEffect, Dispatch, SetStateAction } from 'react'
import { LatLngTuple } from 'leaflet'
import { SelectChangeEvent } from '@mui/material'
import { Marker, Tooltip, Circle, useMapEvent } from 'react-leaflet'
import CustomPoint from '../CustomPoint'
import PointDetails from '../PointDetails'
import EmitterDrawer from '../EmitterDrawer'
import DraggableMarker from '../DraggableMarker'
import Heatmap from '../Heatmap'
import { useEmitters } from '../../../contexts/emitters.context'
import { createHeatmapSimulation } from '../../../services/rf-model.service'
import { IHeatmapRequest } from '../../../../shared/types/api/heatmap.type'
import { ICustomPoint } from '../../../types/emitter.type'
import { ISelectedPoint } from '../types'
import { IFrequencyAssignmentWithCoordinates } from '../../../types/exercise.type'
import { calculateDistance, calculateDestination } from '../../../utils/functions'
import HeatmapSettingsModal from './HeatmapSettingsModal'
import prepareHeatmapParams from '../../../utils/prepareAreaRequestData'

interface IProps {
  points: IFrequencyAssignmentWithCoordinates[]
  groupIndex: number
  isPopupHovered: boolean
  setIsPopupHovered: Dispatch<SetStateAction<boolean>>
  isSelected: boolean
  onSelect: (data: ISelectedPoint) => void
  onDrawerOpenChange: (isOpen: boolean) => void
}

export enum ActionTypes {
  ADD = 'Add',
  NONE = 'None',
  MOVE = 'Move',
  ANALYZE = 'Analyze',
}

const MapEntity: React.FC<IProps> = ({
  points,
  isPopupHovered,
  setIsPopupHovered,
  groupIndex,
  isSelected,
  onSelect,
  onDrawerOpenChange,
}) => {
  const [isMoving, setIsMoving] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [actionType, setActionType] = useState<ActionTypes>(ActionTypes.NONE)
  const [customPoints, setCustomPoints] = useState<ICustomPoint[]>([])
  const [simulationLoading, setSimulationLoading] = useState(false)
  const [isDefaultPointsAdded, setIsDefaultPointsAdded] = useState(false)
  const [showHeatmapSimulation, setShowHeatmapSimulation] = useState(false)
  const [isHeatmapParamsModalOpen, setIsHeatmapParamsModalOpen] = useState(false)

  const {
    selectEmitterData,
    handleChangeDataBy,
    showingRadii,
    showingHeatmaps,
    setShowHeatmap,
    setHeatmap,
    fetchOneHeatmap,
  } = useEmitters()

  // The heatmap(s) for the selected point (singular).
  // Normally there is only one heatmap,
  // but if we are showing a mobile emitter there might be a line of them.
  const selectedHeatmaps = useMemo(() => {
    return points ? points.find((el) => el.selected)?.heatmaps : []
  }, [points])

  const selectedHeatmapSimulation = useMemo(() => {
    return points ? points.find((el) => el.selected)?.heatmapSimulation : null
  }, [points])

  // The specific frequency assignment selected in the dropdown,
  // since a pin can have several:
  const selectedFrequencyAssignment = useMemo<IFrequencyAssignmentWithCoordinates>(
    () => points.find((el) => el.selected)!,
    [points],
  )

  const [position, setPosition] = useState<LatLngTuple>([
    selectedFrequencyAssignment.latitude,
    selectedFrequencyAssignment.longitude,
  ])

  useMapEvent('click', (data) => {
    if (isAdding && data && !isPopupHovered) {
      const { lat, lng } = data.latlng
      const distance = calculateDistance(
        lat,
        lng,
        selectedFrequencyAssignment.latitude,
        selectedFrequencyAssignment.longitude,
      )

      if (distance <= selectedFrequencyAssignment.request_location_radius) {
        handleAddCustomPoint({
          ...selectedFrequencyAssignment,
          id: selectedFrequencyAssignment.id_override + (customPoints.length + 1),
          radius: 10, // Default value
          heatmapParams: prepareHeatmapParams({
            lat,
            lon: lng,
            frq: selectedFrequencyAssignment.request_frequency_value,
            rad: 10,
          }),
          latitude: lat,
          showHeatmap: false,
          longitude: lng,
          selected: false,
          showRadius: false,
          heatmap: null,
          heatmapSimulation: undefined,
        })
      }
    }
  })

  const handleMarkerClick = async () => {
    onSelect({
      index: groupIndex,
      points: points,
      focused: true,
    })
  }

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value
    selectEmitterData(selectedFrequencyAssignment!.id_override, selectedId)
  }

  // TODO: We don't actually save your changes to the backend!
  const handleUpdate = async (updatedPoint: Partial<IFrequencyAssignmentWithCoordinates>) => {
    if (selectedFrequencyAssignment === null) return

    if (updatedPoint.request_location_radius || updatedPoint.request_frequency_value) {
      // Build updated heatmapParams reflecting the new radius/frequency before fetching:
      const updatedHeatmapParams = {
        ...selectedFrequencyAssignment.heatmapParams,
        output: {
          ...selectedFrequencyAssignment.heatmapParams.output,
          ...(updatedPoint.request_location_radius != null ? { rad: updatedPoint.request_location_radius } : {}),
        },
        transmitter: {
          ...selectedFrequencyAssignment.heatmapParams.transmitter,
          ...(updatedPoint.request_frequency_value != null ? { frq: updatedPoint.request_frequency_value } : {}),
        },
      }
      handleChangeDataBy(
        { id_override: selectedFrequencyAssignment.id_override },
        { ...updatedPoint, heatmapParams: updatedHeatmapParams },
      )
      setHeatmap(selectedFrequencyAssignment.id_override, 0, null)

      try {
        const heatmap = await fetchOneHeatmap(selectedFrequencyAssignment.id_override, 0, updatedHeatmapParams)
        setHeatmap(selectedFrequencyAssignment.id_override, 0, heatmap)
      } catch (err: any) {
        // fetchOneHeatmap already shows a toast on failure
        setHeatmap(selectedFrequencyAssignment.id_override, 0, null, { heatmapsLoading: false })
      }
    } else {
      handleChangeDataBy({ id_override: selectedFrequencyAssignment.id_override }, { ...updatedPoint })
    }
  }

  const handleAct = (value: ActionTypes) => {
    setActionType(value)

    switch (value) {
      case ActionTypes.MOVE:
        setIsAdding(false)
        setIsMoving(true)
        break
      case ActionTypes.ADD:
        setIsMoving(false)
        setIsAdding(true)
        break
      case ActionTypes.NONE:
        setIsMoving(false)
        setIsAdding(false)
        break
      default:
        break
    }
  }

  const handleAddCustomPoint = (newPoint: ICustomPoint) => {
    setCustomPoints((prev) => [...prev, newPoint])
  }

  const handleResetLocation = () => {
    const heatmapParams = selectedFrequencyAssignment.heatmapParams

    setIsMoving(false)
    handleChangeDataBy(
      { id_override: selectedFrequencyAssignment.id_override },
      {
        ...selectedFrequencyAssignment,
        heatmapParams: {
          ...heatmapParams,
          transmitter: {
            ...heatmapParams.transmitter,
            lat: selectedFrequencyAssignment.initialLat,
            lon: selectedFrequencyAssignment.initialLong,
          },
        },
        latitude: selectedFrequencyAssignment.initialLat,
        longitude: selectedFrequencyAssignment.initialLong,
      },
    )
    handleMoveDefaultPoints({
      lat: selectedFrequencyAssignment.initialLat,
      lng: selectedFrequencyAssignment.initialLong,
    })
  }

  const handleHeatmapParamsChange = (updatedParams: IHeatmapRequest) => {
    handleChangeDataBy(
      { id_override: selectedFrequencyAssignment.id_override },
      { ...selectedFrequencyAssignment, heatmapParams: updatedParams },
    )
  }

  const handleDragMarker = (center: { lat: number; lng: number }) => {
    const heatmapParams = selectedFrequencyAssignment.heatmapParams

    handleChangeDataBy(
      {
        id_override: selectedFrequencyAssignment.id_override,
      },
      {
        ...selectedFrequencyAssignment,
        heatmapParams: {
          ...heatmapParams,
          transmitter: {
            ...heatmapParams.transmitter,
            lat: center.lat,
            lon: center.lng,
          },
        },
        latitude: center.lat,
        longitude: center.lng,
        heatmaps: null,
      },
    )

    if (isDefaultPointsAdded) {
      handleMoveDefaultPoints(center)
    }

    if (showingRadii[selectedFrequencyAssignment.id_override]) {
      setPosition([center.lat, center.lng])
    }
  }

  const handleMoveDefaultPoints = (center: { lat: number; lng: number }) => {
    setCustomPoints((prev) =>
      prev.map((point) => {
        const newLat = point.latitude + center.lat - selectedFrequencyAssignment.latitude
        const newLong = point.longitude + center.lng - selectedFrequencyAssignment.longitude

        return {
          ...point,
          heatmap: null,
          showHeatmap: false,
          latitude: newLat,
          longitude: newLong,
        }
      }),
    )
  }

  //Creates custom points at the edge for each direction(North, Northwest etc)
  const handleAddDefaultPoints = () => {
    let index = 1
    let direction = 0
    let previousPointCoords: number[] = []

    while (direction !== 405) {
      const { lat, lon } = calculateDestination(
        selectedFrequencyAssignment.latitude,
        selectedFrequencyAssignment.longitude,
        selectedFrequencyAssignment.request_location_radius,
        direction,
      )

      if (direction === 0) {
        direction += 45
        previousPointCoords = [lat, lon]
        continue
      }

      const radius = calculateDistance(lat, lon, previousPointCoords[0], previousPointCoords[1]) / 2

      handleAddCustomPoint({
        radius,
        id: selectedFrequencyAssignment.id_override + index,
        latitude: lat,
        longitude: lon,
        heatmap: null,
        showHeatmap: false,
        heatmapParams: prepareHeatmapParams({
          lat,
          lon,
          frq: selectedFrequencyAssignment.request_frequency_value,
          rad: radius,
        }),
        selected: false,
        showRadius: false,
        request_frequency_value: selectedFrequencyAssignment.request_frequency_value,
      })

      direction += 45
      previousPointCoords = [lat, lon]
      index++
    }

    setIsDefaultPointsAdded(true)
  }

  // TODO: Is caching needed for heatmap simulation, similar to the approach used for heatmaps?
  const handleHeatmapSimulation = async (updatedParams: IHeatmapRequest) => {
    const data = await createHeatmapSimulation(updatedParams)

    if (data.data && data.success) {
      handleChangeDataBy(
        {
          id_override: selectedFrequencyAssignment.id_override,
        },
        {
          heatmapSimulation: data.data,
        },
      )
    }
  }

  const onHeatmapSimulationClick = () => {
    if (showHeatmapSimulation) {
      setShowHeatmapSimulation(false)
    } else {
      setSimulationLoading(true)
      setShowHeatmapSimulation(true)
    }
  }

  const handleCustomPointSelect = (pointId: string) => {
    setCustomPoints((prev) =>
      prev.map((elem) => (elem.id === pointId ? { ...elem, selected: !elem.selected } : { ...elem })),
    )
  }

  const handleCustomPointsRemove = () => {
    setCustomPoints([])
    setIsDefaultPointsAdded(false)
  }

  useEffect(() => {
    if (!isSelected) {
      setIsMoving(false)
    }
  }, [isSelected])

  useEffect(() => {
    if (isDragging && isDefaultPointsAdded) {
      setCustomPoints([])
      setShowHeatmap(selectedFrequencyAssignment.id_override, false)
      setShowHeatmapSimulation(false)
    }
  }, [isDragging])

  useEffect(() => {
    setPosition([selectedFrequencyAssignment.latitude, selectedFrequencyAssignment.longitude])
  }, [selectedFrequencyAssignment.latitude, selectedFrequencyAssignment.longitude])

  if (!selectedFrequencyAssignment) return null

  const showingHeatmap = showingHeatmaps[selectedFrequencyAssignment.id_override]

  return (
    <>
      {isMoving ? (
        <DraggableMarker
          center={position}
          draggable
          setDragging={setIsDragging}
          handleClick={handleMarkerClick}
          handlePositionChange={handleDragMarker}
        >
          <Tooltip>
            <PointDetails point={selectedFrequencyAssignment} />
          </Tooltip>
        </DraggableMarker>
      ) : (
        <Marker position={position} eventHandlers={{ click: handleMarkerClick }}>
          <Tooltip>
            <PointDetails point={selectedFrequencyAssignment} />
          </Tooltip>
        </Marker>
      )}

      {isSelected && (
        <EmitterDrawer
          points={points}
          handleAct={handleAct}
          actionType={actionType}
          customPoints={customPoints}
          onPointSelect={handleCustomPointSelect}
          setCustomPoints={setCustomPoints}
          simulationLoading={simulationLoading}
          setIsPopupHovered={setIsPopupHovered}
          onOpenChange={onDrawerOpenChange}
          handleSelectChange={handleSelectChange}
          handleResetLocation={handleResetLocation}
          isDefaultPointsAdded={isDefaultPointsAdded}
          showHeatmapSimulation={showHeatmapSimulation}
          handleAddDefaultPoints={handleAddDefaultPoints}
          onHeatmapSimulationClick={onHeatmapSimulationClick}
          handleCustomPointsRemove={handleCustomPointsRemove}
          selectedFrequencyAssignment={selectedFrequencyAssignment}
          setIsHeatmapParamsModalOpen={setIsHeatmapParamsModalOpen}
        />
      )}

      {showingRadii[selectedFrequencyAssignment.id_override] &&
        !isDragging &&
        selectedFrequencyAssignment &&
        position && <Circle center={position} radius={selectedFrequencyAssignment.request_location_radius * 1000} />}

      {(selectedHeatmaps || []).map((heatmap, i) => showingHeatmap && <Heatmap key={i} heatmap={heatmap} />)}
      {showHeatmapSimulation && <Heatmap heatmap={selectedHeatmapSimulation} />}

      {customPoints.map((point) => (
        <CustomPoint key={point.id} point={point} onSelect={handleCustomPointSelect} setPoints={setCustomPoints} />
      ))}

      {selectedFrequencyAssignment.heatmapParams && (
        <HeatmapSettingsModal
          initialValues={selectedFrequencyAssignment.heatmapParams}
          onUpdate={handleHeatmapParamsChange}
          isOpen={isHeatmapParamsModalOpen}
          onClose={() => setIsHeatmapParamsModalOpen(false)}
        />
      )}

      <HeatmapSettingsModal
        initialValues={selectedFrequencyAssignment.heatmapParams}
        onUpdate={handleHeatmapSimulation}
        isOpen={simulationLoading}
        onClose={() => setSimulationLoading(false)}
      />
    </>
  )
}

export default memo(MapEntity)
